import { ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { CalendarActivity } from '@/api/resourceCalendarApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ACTIVITY_STYLES, SOURCE_LABEL, STATE_LABELS, canDrag, dayHeader, formatHours } from '@/lib/myTasks'
import { cn } from '@/lib/utils'
import { TypeBadge } from './ActivityCard'

/**
 * An activity's details: the exact due date (cards show only MM/YY), where it
 * comes from, and — for a supervisor — "Change day or owner…", the move that
 * works without dragging. Completing or approving work stays in its own screen.
 */
export function ActivityDialog({
  activity,
  legendName,
  ownerName,
  canManage,
  onMove,
  onClose,
}: {
  activity: CalendarActivity
  legendName: string
  ownerName: string
  canManage: boolean
  onMove: () => void
  onClose: () => void
}) {
  const rows: [string, string][] = [
    ['Type', `${activity.type} · ${legendName}`],
    ['Owner', ownerName],
    ['Day', dayHeader(activity.date)],
    ['Due', activity.dueDate ? `${dayHeader(activity.dueDate)} ${activity.dueDate.slice(0, 4)}` : '—'],
    ['Expected hours', formatHours(activity.hours)],
    ['Time', activity.startTime ? `${activity.startTime}${activity.endTime ? `–${activity.endTime}` : ''}` : '—'],
    ['State', STATE_LABELS[activity.state]],
    ['Source', SOURCE_LABEL[activity.source]],
    ['Reference', activity.sourceRef ?? '—'],
  ]
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn('border-l-4 sm:max-w-md', ACTIVITY_STYLES[activity.type].card)}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <TypeBadge code={activity.type} />
            {activity.description}
          </DialogTitle>
          <DialogDescription>Activity details</DialogDescription>
        </DialogHeader>
        <dl className='grid grid-cols-[7.5rem_1fr] gap-x-3 gap-y-1.5 text-sm'>
          {rows.map(([k, v]) => (
            <div key={k} className='contents'>
              <dt className='text-muted-foreground'>{k}</dt>
              <dd className='text-foreground'>{v}</dd>
            </div>
          ))}
        </dl>
        {!activity.movable && (
          <p className='text-xs text-muted-foreground'>
            This activity is planned in its own screen; open it there to change it.
          </p>
        )}
        <DialogFooter className='gap-2 sm:justify-between'>
          {activity.href ? (
            <Button variant='outline' asChild>
              <Link to={activity.href}>
                Open in its screen <ExternalLink className='ml-1 h-3.5 w-3.5' />
              </Link>
            </Button>
          ) : (
            <span />
          )}
          {canDrag(activity, canManage) && <Button onClick={onMove}>Change day or owner…</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
